import React, { useState, useEffect } from "react";
import { Outlet, useParams, Link, useSearchParams, useLocation } from "react-router-dom";
import { LogOut, Menu, Ticket } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import SidebarDrawer from "./SidebarDrawer";
import axios from "../../utils/axiosConfig";
import { buildApiUrl, showMissingApiUrlAlert } from "../../utils/apiConfig";
import { loadUser } from "../../store/slices/authSlice";
import SubscriptionAlertBanner from "../admin/SubscriptionAlertBanner";
import GlobalOnboardingPrompt from "../onboarding/GlobalOnboardingPrompt";
import PublicFooter from "./PublicFooter";

const Layout = () => {
  const { slug } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, user, loading } = useSelector((s) => s.auth);

  const [open, setOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // Capturar referido si existe en la URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      sessionStorage.setItem(`rrpp_${slug}`, ref);
      console.log(`RRPP atribuido para ${slug}:`, ref);
    }
  }, [searchParams, slug]);

  // Cargar sesión al montar/cambiar slug
  useEffect(() => {
    dispatch(loadUser());
    setOpen(false);
  }, [dispatch, slug]);

  const googleLoginUrl = buildApiUrl(`/api/auth/google?slug=${encodeURIComponent(slug || '')}`);
  const hideFooter = /\/admin(\/|$)|\/rrpp(\/|$)|\/profile(\/|$)|\/projection(\/|$)|\/broadcaster(\/|$)|\/live-access(\/|$)|\/upload(\/|$)/.test(location.pathname);

  const handleLogout = async () => {
    try {
      await axios.post("/api/auth/logout", { withCredentials: true });
    } catch (e) {
      // no-op
    } finally {
      dispatch(loadUser());
    }
  };

  return (
    <div className="min-h-screen">
      <SubscriptionAlertBanner />
      <GlobalOnboardingPrompt />
      {/* Header superior */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur border-b border-white/5 mt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-14 flex items-center justify-between gap-3">
          {/* Izquierda: botón hamburguesa + marca */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setOpen(true)}
              className="p-2 -ml-2 rounded hover:bg-white/10 text-white"
              aria-label="Abrir menú"
            >
              <Menu size={22} />
            </button>
            
            {/* Logo de PASSAR (Volver a Inicio) */}
            <Link to="/" className="flex items-center gap-1.5 group shrink-0" title="Volver a Inicio">
               <div className="w-8 h-8 bg-brand-gradient rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(255,107,0,0.2)] group-hover:scale-110 transition-transform">
                 <Ticket className="text-white" size={16} />
               </div>
               <span className="font-black tracking-tighter text-lg text-white hidden sm:block group-hover:text-primary-orange transition-colors">PASSAR</span>
            </Link>
            
            <span className="text-white/20 hidden sm:block">/</span>

            {/* Nombre del Salón */}
            <Link to={`/${slug}`} className="font-semibold text-primary-orange text-lg hover:opacity-80 transition-opacity max-w-[82px] sm:max-w-none truncate">
              {slug}
            </Link>
          </div>

          {/* Derecha: acciones (Iniciar/Cerrar sesión) */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {loading ? (
              <span className="text-sm text-gray-500">Cargando…</span>
            ) : isAuthenticated ? (
              <>
                {/* 👑 Botón SuperAdmin (Leonardo) */}
                {user?.email === 'leonardodavidcba@gmail.com' && (
                   <Link 
                     to="/superadmin/dashboard" 
                     className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-tighter bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500 hover:text-white transition-all"
                   >
                     Panel SuperAdmin
                   </Link>
                )}

                {/* 🏢 Botón Volver a mi Salón (Para Admins navegando otros salones) */}
                {user?.role === 'admin' && user?.salonSlug && user?.salonSlug !== slug && (
                   <Link 
                     to={`/${user.salonSlug}`} 
                     className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-tighter bg-primary-orange/10 text-primary-orange border border-primary-orange/20 hover:bg-primary-orange hover:text-white transition-all"
                   >
                     Volver a mi Salón
                   </Link>
                )}

                {/* (Opcional) avatar / nombre */}
                <Link
                  to={`/${slug}/profile`}
                  className="hidden md:inline text-sm text-white/80 hover:text-primary-orange"
                  title={user?.name || "Perfil"}
                >
                  {user?.name || "Mi perfil"}
                </Link>
                <button
                  onClick={handleLogout}
                  className="h-10 w-10 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 rounded-xl sm:rounded-lg text-sm border border-red-500/30 bg-red-500/10 text-red-500 hover:bg-red-500/20 flex items-center justify-center gap-2"
                  aria-label="Cerrar sesión"
                  title="Cerrar sesión"
                >
                  <LogOut size={18} />
                  <span className="hidden sm:inline">Cerrar sesión</span>
                </button>
              </>
            ) : (
              <a
                href={googleLoginUrl || "#"}
                onClick={(e) => {
                  if (!googleLoginUrl) {
                    e.preventDefault();
                    showMissingApiUrlAlert();
                  }
                }}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-brand-gradient text-white hover:opacity-90 shadow-lg shadow-orange-500/20 transition-all active:scale-95"
              >
                Iniciar sesión
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar Drawer */}
      <SidebarDrawer isOpen={open} onClose={() => setOpen(false)} />

      {/* Contenido */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Outlet />
      </main>

      {!hideFooter && <PublicFooter salonSlug={slug} />}
    </div>
  );
};

export default Layout;
